/**
 * Die Zelle: eingeschränkte Shell links, Tür mit vier Schlössern rechts.
 *
 * Die Shell läuft im Browser auf den Spuren, die der Server geschickt hat –
 * die Antworten kennt nur der Server. Jedes Schloss wird einzeln geprüft.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import clsx from 'clsx';
import { ESCAPE, runEscapeCommand, type EscapePuzzle } from '@hosting-sim/core';
import { Button } from '../ui';
import type { MinigameApi } from './MinigameOverlay';
import { CellScene } from './CellScene';

const SUGGESTIONS = ['help', 'ls', 'cat NOTIZ.txt', 'ps aux', 'ss -tlnp', 'cat /var/log/auth.log', 'crontab -l -u www-data'];

interface Line {
  id: number;
  text: string;
  input?: boolean;
}

let lineCounter = 0;

export function EscapeGame({
  puzzle,
  solved: initiallySolved,
  api,
  disabled,
}: {
  puzzle: EscapePuzzle;
  solved: number[];
  api: MinigameApi;
  disabled: boolean;
}) {
  const [lines, setLines] = useState<Line[]>(() => [
    { id: ++lineCounter, text: 'Verbindung hergestellt … Moment. Das ist nicht deine Shell.' },
    { id: ++lineCounter, text: `chroot: /srv/zelle auf ${puzzle.hostname} – „help“ zeigt, was hier geht.` },
    { id: ++lineCounter, text: '' },
  ]);
  const [input, setInput] = useState('');
  const [solved, setSolved] = useState<number[]>(initiallySolved);
  const [answers, setAnswers] = useState<string[]>(() => puzzle.locks.map(() => ''));
  /** Bereits falsch gewählte Antworten je Schloss – werden durchgestrichen. */
  const [wrong, setWrong] = useState<Record<number, string[]>>({});
  const [feedback, setFeedback] = useState<Record<number, { ok: boolean; text: string }>>({});
  const [busyLock, setBusyLock] = useState<number | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [lines]);

  const prompt = `admin@${puzzle.hostname}:/srv/zelle$`;

  function exec(line: string) {
    const trimmed = line.trim();
    setInput('');
    setCursor(null);
    if (trimmed) setHistory((h) => [...h, trimmed].slice(-50));
    const result = runEscapeCommand(puzzle, trimmed);
    if (result.clear) {
      setLines([]);
      return;
    }
    setLines((list) =>
      [...list, { id: ++lineCounter, text: trimmed, input: true }, ...result.output.map((text) => ({ id: ++lineCounter, text }))].slice(-300),
    );
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      exec(input);
    } else if (event.key === 'ArrowUp' && history.length > 0) {
      event.preventDefault();
      const next = cursor === null ? history.length - 1 : Math.max(0, cursor - 1);
      setCursor(next);
      setInput(history[next]);
    } else if (event.key === 'ArrowDown' && cursor !== null) {
      event.preventDefault();
      const next = cursor + 1;
      setCursor(next >= history.length ? null : next);
      setInput(next >= history.length ? '' : history[next]);
    }
  }

  async function tryLock(lock: number, choice?: string) {
    const answer = choice ?? answers[lock];
    if (disabled || busyLock !== null || !answer.trim()) return;
    setBusyLock(lock);
    const result = await api.submit({ lock, answer });
    setBusyLock(null);
    if (result.ok) {
      const data = result.data as { solved?: number[] } | undefined;
      const next = data?.solved ?? [...solved, lock];
      setSolved(next);
      setFeedback((f) => ({ ...f, [lock]: { ok: true, text: 'Klick – offen.' } }));
      if (next.length >= puzzle.locks.length) api.finish(true, result.message);
    } else {
      setWrong((w) => ({ ...w, [lock]: [...(w[lock] ?? []), answer] }));
      setFeedback((f) => ({ ...f, [lock]: { ok: false, text: result.message } }));
    }
  }

  const open = solved.length;
  const total = puzzle.locks.length;

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_400px]">
      {/* Shell */}
      <div className="min-w-0 space-y-2">
        <div
          ref={scrollRef}
          onClick={() => inputRef.current?.focus()}
          className="h-[420px] overflow-y-auto rounded-md border border-zinc-700 bg-black p-3 font-mono text-xs leading-relaxed text-zinc-200"
        >
          {lines.map((line) => (
            <div key={line.id} className="whitespace-pre-wrap break-all">
              {line.input ? (
                <>
                  <span className="text-rose-400">{prompt}</span> {line.text}
                </>
              ) : (
                line.text || ' '
              )}
            </div>
          ))}
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-rose-400">{prompt}</span>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={disabled}
              autoFocus
              spellCheck={false}
              autoComplete="off"
              aria-label="Shell in der Zelle"
              className="min-w-0 flex-1 border-0 bg-transparent p-0 font-mono text-xs text-zinc-100 caret-rose-400 outline-none focus:ring-0"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              disabled={disabled}
              onClick={() => exec(s)}
              className="rounded border border-zinc-700 px-2 py-0.5 font-mono text-2xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Zellentür */}
      <div className="rounded-lg border-4 border-zinc-600 bg-gradient-to-b from-zinc-700 to-zinc-800 p-3">
        <div className="mb-3">
          <CellScene
            total={total}
            solved={solved}
            active={null}
            onSelect={(lock) => document.getElementById(`zelle-schloss-${lock}`)?.focus()}
          />
          <p className="mt-1 text-right text-xs text-zinc-300">
            {open}/{total} Schlösser offen
          </p>
        </div>
        {/* Gitterstäbe als Hintergrund */}
        <div
          className="space-y-2 rounded-md p-2"
          style={{
            backgroundImage: 'repeating-linear-gradient(90deg, rgba(161,161,170,0.35) 0 6px, transparent 6px 34px)',
          }}
        >
          {puzzle.locks.map((lock, i) => {
            const isOpen = solved.includes(i);
            const fb = feedback[i];
            return (
              <div
                key={i}
                className={clsx(
                  'rounded-md border p-2 backdrop-blur-sm transition',
                  isOpen ? 'border-emerald-500 bg-emerald-950/80' : 'border-zinc-500 bg-zinc-900/90',
                )}
              >
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-lg" aria-hidden>
                    {isOpen ? '🔓' : '🔒'}
                  </span>
                  <span className="text-sm font-medium">
                    {i + 1}. {lock.question}
                  </span>
                </div>
                {!isOpen && lock.choices && (
                  <div className="space-y-2">
                    <p className="text-xs leading-relaxed text-zinc-300">{lock.explain}</p>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => exec(lock.command)}
                      className="rounded border border-sky-700 bg-sky-950/60 px-2 py-1 font-mono text-2xs text-sky-200 hover:bg-sky-900 disabled:opacity-50"
                    >
                      ▶ {lock.command}
                    </button>
                    <div id={`zelle-schloss-${i}`} tabIndex={-1} className="grid grid-cols-2 gap-1.5 outline-none">
                      {lock.choices.map((choice) => {
                        const failed = wrong[i]?.includes(choice);
                        return (
                          <button
                            key={choice}
                            type="button"
                            disabled={disabled || failed || busyLock !== null}
                            onClick={() => void tryLock(i, choice)}
                            className={clsx(
                              'rounded border px-2 py-1.5 font-mono text-sm transition',
                              failed
                                ? 'border-red-900 bg-red-950/40 text-red-400 line-through'
                                : 'border-zinc-600 bg-zinc-950 text-zinc-100 hover:border-amber-400 hover:bg-zinc-800',
                            )}
                          >
                            {choice}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Ältere Rätsel ohne Auswahl: freie Eingabe wie bisher. */}
                {!isOpen && !lock.choices && (
                  <form
                    className="flex gap-1.5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void tryLock(i);
                    }}
                  >
                    <input
                      id={`zelle-schloss-${i}`}
                      value={answers[i]}
                      onChange={(e) => setAnswers((a) => a.map((v, j) => (j === i ? e.target.value : v)))}
                      disabled={disabled}
                      spellCheck={false}
                      autoComplete="off"
                      className="min-w-0 flex-1 rounded border border-zinc-600 bg-zinc-950 px-2 py-1 font-mono text-sm text-zinc-100 outline-none focus:border-amber-400"
                      aria-label={lock.question}
                    />
                    <Button size="sm" variant="primary" type="submit" loading={busyLock === i} disabled={disabled}>
                      Öffnen
                    </Button>
                  </form>
                )}
                {fb && <p className={clsx('mt-1 text-xs', fb.ok ? 'text-emerald-400' : 'text-red-400')}>{fb.text}</p>}
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-2xs text-zinc-400">Jede falsche Antwort kostet {ESCAPE.PENALTY_SECONDS} Sekunden.</p>
      </div>
    </div>
  );
}
